import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormError,
  FormLabel,
  Heading,
  Input,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Option,
  Select,
  ToastType,
} from '@alefeducation/ui-elements-library-core';
import { Times } from '@alefeducation/ui-elements-library-icons';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { ApolloError } from '@apollo/client/errors';
import TenantDropdown from './TenantDropdown';
import VirtualTenantDropDown from './VirtualTenantDropDown';
import {
  CREATE_ORGANISATION,
  UPDATE_ORGANISATION,
} from '../../apollo/mutations';
import { Organisation } from '../../apollo/queries';
import {
  countries,
  getCountryCode,
  getCountryName,
} from '../../utils/countries';
import { showAutoHideToastOfType } from '../../utils/Toast';
import {
  isAlphaNumeric,
  isAlphaNumericIncludingSpaces,
} from '../../utils/isAlphaNumeric';
import { isEmailValid } from '../../utils/Validators';
import {
  ADD_ORG_BUTTON_TEXT,
  ADD_ORGANISATION,
  DUPLICATE_ORG_CODE,
  DUPLICATE_ORG_CODE_MESSAGE,
  DUPLICATE_ORG_NAME,
  DUPLICATE_ORG_NAME_MESSAGE,
  DUPLICATE_SOURCE_ID,
  DUPLICATE_SOURCE_ID_MESSAGE,
  EDIT_ORGANISATION,
  ERROR_OCCURRED_WHILE_ADDING,
  ERROR_OCCURRED_WHILE_EDITING,
  INVALID_EMAIL,
  ORG_NAME_VALIDATION_TEXT,
  ORGANISATION_SUCCESSFULLY_ADDED,
  ORGANISATION_SUCCESSFULLY_UPDATED,
  SAVE,
  VIEW_ORGANISATION,
} from '../../utils/constants';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';
import { VirtualTenant } from '../../apollo/queries/virtualTenant/getVirtualTenants.queries';

interface Props {
  open: boolean;
  setShowModal: (val: boolean) => void;
  organisation?: Organisation;
  isEdit?: boolean;
  isViewOnlyRole?: boolean;
}

const StyledHeaderContainer = styled.div`
  margin-bottom: ${({ theme: { space } }: any) => `${space[14]}px`};
`;

export const StyledLabelContainer = styled.div`
  margin-bottom: ${({ theme: { space } }: any) => `${space[8]}px`};
`;

const StyledFormControlContainer = styled.div`
  margin-bottom: ${({ theme: { space } }: any) => `${space[16]}px`};

  div[data-testid='select-country'],
  div[data-testid='select-tenant'] {
    position: static;
  }
`;

const defaultOrg: Organisation = {
  uuid: '',
  email: '',
  name: '',
  code: '',
  country: '',
  tenantCode: '',
  isActive: false,
};

const StyledModal = styled(Modal)`
  .MuiDialog-paper {
    position: static;
  }

  .fade-effect {
    display: none;
  }
`;

export const OrganisationEditorModal: React.FC<Props> = ({
  open,
  setShowModal,
  organisation = defaultOrg,
  isViewOnlyRole,
  isEdit,
}) => {
  const [name, setOrgName] = useState(organisation.name || '');
  const [uuid, setUuid] = useState(organisation.uuid || '');
  const [email, setEmail] = useState(organisation.email || '');
  const [code, setCode] = useState(organisation.code || '');
  const [sourceId, setSourceId] = useState(organisation.sourceId);
  const [country, setCountry] = useState(organisation.country || '');
  const [tenantCode, setTenantCode] = useState(organisation.tenantCode || '');
  const [virtualTenant, setVirtualTenant] = useState<VirtualTenant | undefined>(
    organisation.virtualTenant,
  );
  const [isOrgNameDuplicate, setIsOrgNameDuplicate] = useState(false);
  const [isOrgCodeDuplicate, setIsOrgCodeDuplicate] = useState(false);
  const [isSourceIdDuplicate, setIsSourceIdDuplicate] = useState(false);

  const isTenantAssociationEnabled =
    FEATURE_TOGGLE.ENABLE_ORG_TENANT_ASSOCIATION;
  const showVirtualTenants = isTenantAssociationEnabled && tenantCode;

  useEffect(() => {
    setOrgName(organisation.name);
    setUuid(organisation.uuid);
    setEmail(organisation.email);
    setCode(organisation.code);
    setCountry(organisation.country);
    setSourceId(organisation.sourceId);
    setVirtualTenant(organisation.virtualTenant);

    if (isTenantAssociationEnabled && organisation?.tenantCode) {
      setTenantCode(organisation.tenantCode);
    }
  }, [organisation]);

  const handleMutationError = (error: ApolloError) => {
    if (error.message === DUPLICATE_ORG_NAME) {
      setIsOrgNameDuplicate(true);
    } else if (error.message === DUPLICATE_ORG_CODE) {
      setIsOrgCodeDuplicate(true);
    } else if (error.message === DUPLICATE_SOURCE_ID) {
      setIsSourceIdDuplicate(true);
    } else {
      setShowModal(false);
    }
  };

  const clearDuplicateError = () => {
    isOrgNameDuplicate && setIsOrgNameDuplicate(false);
    isOrgCodeDuplicate && setIsOrgCodeDuplicate(false);
    isSourceIdDuplicate && setIsSourceIdDuplicate(false);
  };

  const [add, addResult] = useMutation(CREATE_ORGANISATION, {
    onCompleted: () => {
      setShowModal(false);
      resetFields();
      clearDuplicateError();
    },
    onError: handleMutationError,
    refetchQueries: ['getOrganisations'],
  });
  const displayAlphaNumericError = code !== '' && !isAlphaNumeric(code);

  const isOrgNameValidationError =
    name !== '' &&
    (name.length < 3 ||
      name.length > 128 ||
      !isAlphaNumericIncludingSpaces(name));
  const isEmailValidationError = email !== '' && !isEmailValid(email);
  const validateFields = () =>
    name === '' ||
    email === '' ||
    code === '' ||
    country === '' ||
    isOrgNameValidationError ||
    isEmailValidationError ||
    !isAlphaNumeric(code);

  const isFieldsChanged = () =>
    name === organisation?.name &&
    email === organisation?.email &&
    sourceId === organisation?.sourceId &&
    country === organisation?.country;

  const validateFieldsForEditOrg = () =>
    validateFields() ||
    (!organisation?.tenantCode
      ? isFieldsChanged() && tenantCode === ''
      : isFieldsChanged() && tenantCode === organisation?.tenantCode);

  const [edit, editResult] = useMutation(UPDATE_ORGANISATION, {
    onCompleted: () => {
      setShowModal(false);
      resetFields();
      clearDuplicateError();
    },
    onError: (error: ApolloError) => {
      clearDuplicateError();
      handleMutationError(error);
    },
    refetchQueries: ['getOrganisations'],
  });

  const addOrganisation = () => {
    const orgSchema: Omit<Organisation, 'uuid'> & { virtualTenantId?: string } =
      {
        name,
        email,
        sourceId,
        isActive: true,
        code,
        country,
      };

    if (isTenantAssociationEnabled) {
      orgSchema.tenantCode = tenantCode;
    }

    if (isTenantAssociationEnabled) {
      orgSchema.virtualTenantId = virtualTenant?.uuid;
    }

    add({
      variables: {
        orgSchema,
      },
    });
  };

  const editOrganisation = () => {
    const orgSchema: Omit<Organisation, 'uuid' | 'code'> = {
      name,
      email,
      sourceId,
      isActive: organisation?.isActive,
      country,
    };

    if (isTenantAssociationEnabled) {
      orgSchema.tenantCode = tenantCode;
    }

    edit({
      variables: {
        uuid: uuid,
        orgSchema: orgSchema,
      },
    });
  };
  const resetFields = () => {
    setOrgName('');
    setEmail('');
    setCode('');
    setCountry('');
    setTenantCode('');
    setSourceId('');
  };

  const resetToOriginal = () => {
    setOrgName(organisation.name);
    setUuid(organisation.uuid);
    setEmail(organisation.email);
    setCode(organisation.code);
    setCountry(organisation.country);
    setSourceId(organisation.sourceId);
  };

  const frameHeaderText = () => {
    if (isEdit) {
      if (isViewOnlyRole) {
        return VIEW_ORGANISATION;
      } else {
        return EDIT_ORGANISATION;
      }
    } else {
      return ADD_ORGANISATION;
    }
  };

  return (
    <div>
      <StyledModal
        size="medium"
        handleBackdropClick={() => {}}
        data-testid="add-organisation-modal"
        open={open}
      >
        <ModalHeader>
          <Times
            role="icon"
            aria-label="cross"
            color="primary.40"
            height="14"
            width="14"
            cursor="pointer"
            onClick={() => setShowModal(false)}
          />
        </ModalHeader>
        <ModalContent>
          <StyledHeaderContainer>
            <Heading variant="heading-small" weight="bold" color="text.100">
              {frameHeaderText()}
            </Heading>
          </StyledHeaderContainer>
          <StyledFormControlContainer>
            <FormControl
              appearance={
                isOrgNameValidationError || isOrgNameDuplicate
                  ? 'error'
                  : 'regular'
              }
            >
              <StyledLabelContainer>
                <Label title="Organisation name*" htmlFor="organisation-name" />
              </StyledLabelContainer>
              <Input
                name="organisation-name"
                placeholder="Organisation Name"
                value={name}
                onChange={(e) => {
                  setOrgName(e.target.value);
                }}
                data-testid={isEdit ? 'edit-org-name' : 'add-org-name'}
              />
              {isOrgNameValidationError && (
                <FormError data-testid={'org-name-validation-error'}>
                  {ORG_NAME_VALIDATION_TEXT}
                </FormError>
              )}
              {isOrgNameDuplicate && (
                <FormError data-testid={'org-name-validation-error'}>
                  {DUPLICATE_ORG_NAME_MESSAGE}
                </FormError>
              )}
            </FormControl>
          </StyledFormControlContainer>
          <StyledFormControlContainer>
            <FormControl
              appearance={
                displayAlphaNumericError || isOrgCodeDuplicate
                  ? 'error'
                  : 'regular'
              }
            >
              <FormLabel>Code*</FormLabel>
              <Input
                value={code}
                name="code"
                placeholder="Create a code"
                disabled={isEdit}
                onChange={(e) => setCode(e.target.value)}
                data-testid={isEdit ? 'edit-org-code' : 'add-org-code'}
              />
              {displayAlphaNumericError && (
                <FormError data-testid={'org-code-error'}>
                  Please enter an alphanumeric code without spaces.
                </FormError>
              )}
              {isOrgCodeDuplicate && (
                <FormError data-testid={'org-code-error'}>
                  {DUPLICATE_ORG_CODE_MESSAGE}
                </FormError>
              )}
            </FormControl>
          </StyledFormControlContainer>
          <StyledFormControlContainer>
            <FormControl
              appearance={
                displayAlphaNumericError || isSourceIdDuplicate
                  ? 'error'
                  : 'regular'
              }
            >
              <FormLabel>Source ID</FormLabel>
              <Input
                value={sourceId}
                name="Source ID"
                placeholder="Source ID"
                onChange={(e) =>
                  setSourceId(e.target.value ? e.target.value : undefined)
                }
                data-testid={isEdit ? 'edit-source-id' : 'add-source-id'}
              />
              {isSourceIdDuplicate && (
                <FormError data-testid={'source-id-error'}>
                  {DUPLICATE_SOURCE_ID_MESSAGE}
                </FormError>
              )}
            </FormControl>
          </StyledFormControlContainer>
          <StyledFormControlContainer>
            <FormControl
              appearance={isEmailValidationError ? 'error' : 'regular'}
            >
              <StyledLabelContainer>
                <Label title="Contact e-mail*" htmlFor="email" />
              </StyledLabelContainer>
              <Input
                name="email"
                placeholder="Contact e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={isEdit ? 'edit-org-email' : 'add-org-email'}
              />
              {isEmailValidationError && (
                <FormError data-testid={'email-error'}>
                  {INVALID_EMAIL}
                </FormError>
              )}
            </FormControl>
          </StyledFormControlContainer>
          <StyledFormControlContainer>
            <StyledLabelContainer>
              <Label title="Country*" />
            </StyledLabelContainer>
            <Select
              searchable
              data-testid="select-country"
              selectedOption={getCountryName(country) || ''}
              onSelectChange={(id, name) => setCountry(getCountryCode(name)!)}
              placeholder="Select country"
            >
              {countries.map((country) => (
                <Option key={country.code}>{country.name}</Option>
              ))}
            </Select>
          </StyledFormControlContainer>
          {isTenantAssociationEnabled && (
            <StyledFormControlContainer>
              <TenantDropdown
                onChange={setTenantCode}
                selectedTenantCode={tenantCode}
                data-testid="tenant-dropdown-id"
              />
            </StyledFormControlContainer>
          )}
          {showVirtualTenants && (
            <StyledFormControlContainer>
              <VirtualTenantDropDown
                disabled={isEdit}
                tenantCode={tenantCode}
                selectedVirtualTenant={virtualTenant}
                onChange={setVirtualTenant}
                data-testid="virtual-tenant-dropdown-id"
              />
            </StyledFormControlContainer>
          )}
        </ModalContent>
        <ModalActions>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              resetToOriginal();
              clearDuplicateError();
            }}
            data-testid={
              isEdit ? 'edit-org-cancel-button' : 'add-org-cancel-button'
            }
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              isEdit ? await editOrganisation() : await addOrganisation();
            }}
            disabled={
              (!isEdit && validateFields()) ||
              (isEdit ? editResult.loading : addResult.loading) ||
              (isEdit && validateFieldsForEditOrg()) ||
              isViewOnlyRole
            }
            data-testid={
              isEdit ? 'edit-org-save-button' : 'add-org-save-button'
            }
          >
            {isEdit ? SAVE : ADD_ORG_BUTTON_TEXT}
          </Button>
        </ModalActions>
      </StyledModal>
      {addResult.error &&
        showAutoHideToastOfType(ERROR_OCCURRED_WHILE_ADDING, ToastType.ERROR)}
      {editResult.error &&
        showAutoHideToastOfType(ERROR_OCCURRED_WHILE_EDITING, ToastType.ERROR)}
      {editResult.data &&
        showAutoHideToastOfType(
          ORGANISATION_SUCCESSFULLY_UPDATED,
          ToastType.SUCCESS,
        )}
      {addResult.data &&
        showAutoHideToastOfType(
          ORGANISATION_SUCCESSFULLY_ADDED,
          ToastType.SUCCESS,
        )}
    </div>
  );
};
